import { trackEvent } from '@/utilities/analytics/trackEvent'

export const IMPERSONATION_HANDOFF_TIMEOUT_MS = 15_000

type ImpersonationHandoffPhase = 'idle' | 'in_progress' | 'awaiting_member'

let impersonationHandoffPhase: ImpersonationHandoffPhase = 'idle'
let impersonationHandoffExpectedMemberId: string | null = null
let impersonationHandoffTimeoutId: ReturnType<typeof setTimeout> | null = null
const impersonationHandoffListeners = new Set<() => void>()

export function isImpersonationHandoffInProgress(): boolean {
  return impersonationHandoffPhase !== 'idle'
}

export function getImpersonationHandoffExpectedMemberId(): string | null {
  return impersonationHandoffExpectedMemberId
}

export function subscribeImpersonationHandoff(onStoreChange: () => void): () => void {
  impersonationHandoffListeners.add(onStoreChange)
  return () => {
    impersonationHandoffListeners.delete(onStoreChange)
  }
}

function notifyImpersonationHandoffListeners(): void {
  for (const listener of impersonationHandoffListeners) {
    listener()
  }
}

function clearImpersonationHandoffTimeout(): void {
  if (impersonationHandoffTimeoutId == null) return
  clearTimeout(impersonationHandoffTimeoutId)
  impersonationHandoffTimeoutId = null
}

function scheduleImpersonationHandoffTimeout(): void {
  clearImpersonationHandoffTimeout()
  impersonationHandoffTimeoutId = setTimeout(() => {
    impersonationHandoffTimeoutId = null
    if (impersonationHandoffPhase !== 'awaiting_member') {
      return
    }
    trackEvent('impersonation:handoff_timeout')
    endImpersonationHandoff()
  }, IMPERSONATION_HANDOFF_TIMEOUT_MS)
}

function setImpersonationHandoffPhase(
  next: ImpersonationHandoffPhase,
  expectedMemberId: string | null = null,
): void {
  if (
    impersonationHandoffPhase === next &&
    impersonationHandoffExpectedMemberId === expectedMemberId
  ) {
    return
  }

  impersonationHandoffPhase = next
  impersonationHandoffExpectedMemberId = expectedMemberId

  if (next === 'awaiting_member') {
    scheduleImpersonationHandoffTimeout()
  } else {
    clearImpersonationHandoffTimeout()
  }

  notifyImpersonationHandoffListeners()
}

export function beginImpersonationHandoff(): void {
  setImpersonationHandoffPhase('in_progress')
}

export function awaitImpersonationHandoffMember(memberId: string): void {
  setImpersonationHandoffPhase('awaiting_member', memberId)
}

export function endImpersonationHandoff(): void {
  setImpersonationHandoffPhase('idle')
}
